import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import en from '../i18n/en.json';
import da from '../i18n/da.json';
import de from '../i18n/de.json';
import fr from '../i18n/fr.json';
import { API_URL } from '../lib/api';

/*
 * v1 sprog-strategi (se docs/language_strategy.md):
 *   - Aktive sprog: en, da
 *   - Vinterhvile: de (genaktiveres v2 ved 50+ betalende EN-brugere + 5+ DE-leads)
 *   - Vinterhvile: fr (genaktiveres tidligst v3 efter paid-marketing-test)
 *
 * `translations` bevarer alle fire sprog så vi ikke breaker eksisterende
 * brugere der har preferred_language='de' eller 'fr' gemt i deres profil
 * — t() kan stadig slå op i de gamle filer hvis backend sender et af de
 * vinterhvile-sprog tilbage. `LANGUAGES`-arrayet styrer derimod kun hvad
 * der er synligt i language-switcher dropdown.
 */
const translations = { en, da, de, fr };

const LanguageContext = createContext(null);

export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' }
];

// Exposed for debug / future re-activation. Not used by switcher in v1.
export const LANGUAGES_HIBERNATING = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

function detectBrowserLanguage() {
  // v1: kun aktive sprog kan auto-detekteres. En tysk browser falder
  // tilbage til engelsk (vores Spor A er global og engelsk-fluent).
  const activeCodes = new Set(LANGUAGES.map(l => l.code));
  const browserLangs = navigator.languages || [navigator.language || 'en'];
  for (const lang of browserLangs) {
    const code = lang.split('-')[0].toLowerCase();
    if (activeCodes.has(code)) return code;
  }
  return 'en';
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('lifemap_language');
    if (saved && translations[saved]) return saved;
    return detectBrowserLanguage();
  });
  const [hasSynced, setHasSynced] = useState(false);

  // Set document lang on initial load
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    // v1: kun aktive sprog kan vælges via UI. Hvis kaldet kommer fra
    // backend-sync (eksisterende profil med preferred_language='de'),
    // tillader vi det stadig så brugeren ikke pludselig skifter sprog
    // bag deres ryg — translations[lang] dækker alle 4 sprog.
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('lifemap_language', lang);
      localStorage.setItem('lifemap_language_explicit', '1');
      document.documentElement.lang = lang;
      // Sync to backend so AI-generated content uses the right language
      axios.put(`${API_URL}/api/profile`, { preferred_language: lang }, { withCredentials: true }).catch(() => {});
    }
  }, []);

  // Listen for cross-context "language-sync-from-profile" events fired by
  // AuthContext when the authenticated user's preferred_language differs from
  // the local UI language and the user hasn't explicitly picked one.
  useEffect(() => {
    const handler = (e) => {
      const lang = e.detail;
      if (translations[lang]) {
        setLanguageState(lang);
        localStorage.setItem('lifemap_language', lang);
        document.documentElement.lang = lang;
      }
    };
    window.addEventListener('language-sync-from-profile', handler);
    return () => window.removeEventListener('language-sync-from-profile', handler);
  }, []);

  // Sync local language to backend ONLY if the user explicitly picked one.
  // Without this guard, opening the dashboard on a fresh browser would
  // overwrite a user's stored preferred_language with the browser default.
  const syncLanguageToBackend = useCallback(() => {
    if (hasSynced) return;
    if (localStorage.getItem('lifemap_language_explicit') !== '1') {
      setHasSynced(true);
      return;
    }
    axios.put(`${API_URL}/api/profile`, { preferred_language: language }, { withCredentials: true })
      .then(() => setHasSynced(true))
      .catch(() => {});
  }, [language, hasSynced]);

  // Get nested translation value by key path (e.g., "auth.signIn")
  const t = useCallback((key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations['en'];
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = value[k2];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }
    
    // Handle string replacements
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([placeholder, replacement]) => {
        value = value.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), replacement);
      });
    }
    
    return (value !== undefined && value !== null) ? value : key;
  }, [language]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    t,
    LANGUAGES,
    translations: translations[language],
    syncLanguageToBackend
  }), [language, setLanguage, t, syncLanguageToBackend]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
